import React from 'react';
import { Progress } from 'antd';
import Button from '../../../../components/Button';
import { PRODUCT_CATEGORY_LINK_BULK_GUIDE_TEXT } from '../../../../constants/service/app/productCategoryLinkBulkConstants';
import { resolveBulkUploadErrorMessage } from '../../../../lib/utils/productCategoryLinkBulkUtils';
import {
  BULK_UPLOAD_ES_SYNC_STATUS,
  BULK_UPLOAD_JOB_EXPIRED,
  BULK_UPLOAD_JOB_STATUS,
  BulkUploadEsSyncSummary,
  BulkUploadJobQueryData,
  BulkUploadJobResult,
} from '../../../../types/category/bulkUpload';
import './ProductCategoryLinkBulkStatusCard.scss';

export type ProductCategoryLinkBulkStatusCardProps = {
  /** 진행 중인 잡 (없으면 카드를 그리지 않는다) */
  jobId: string | null;
  fileName: string;
  /** 파일 전송 중일 때의 전송률(0~100). 전송이 끝나면 null */
  uploadPercent: number | null;
  job: BulkUploadJobQueryData | undefined;
  /** 잡 상태를 한 번도 받지 못한 채 조회가 실패한 상태 */
  isStatusUnavailable: boolean;
  isDownloadingFailedCsv: boolean;
  onDownloadFailedCsv: () => void;
  onDismiss: () => void;
};

const formatCount = (count: number): string => count.toLocaleString();

type ResultSummaryProps = {
  result: BulkUploadJobResult;
};

const ResultSummary = (props: ResultSummaryProps): JSX.Element => {
  const { totalRows, successRows, alreadyExistsRows, failRows } = props.result;

  return (
    <ul className="result-summary">
      <li>총 {formatCount(totalRows)}행</li>
      <li>
        {/*
          이미 존재하는 링크는 사용자 의도가 이미 만족된 상태이므로 성공에 합산한다(스펙 §5.2).
          운영 가시성을 위해 신규 INSERT 와 분리해 괄호로 보여준다.
        */}
        성공 {formatCount(successRows + alreadyExistsRows)} (신규 {formatCount(successRows)} · 이미 존재{' '}
        {formatCount(alreadyExistsRows)})
      </li>
      <li className={failRows > 0 ? 'is-fail' : undefined}>실패 {formatCount(failRows)}</li>
    </ul>
  );
};

type EsSyncGuideProps = {
  esSync: BulkUploadEsSyncSummary | undefined;
};

/**
 * 완료 카드의 검색 인덱스 동기화 안내.
 * 메인 잡 completed 는 DB 반영까지만이고 ES 는 별도 잡으로 이어진다(스펙 §8.2).
 * 백엔드가 EsSyncOutbox 집계를 내려주므로(#4914) 고정 문구 대신 실제 수치를 보여준다.
 */
const EsSyncGuide = (props: EsSyncGuideProps): JSX.Element | null => {
  const { esSync } = props;

  // 집계가 없으면(구버전 응답 방어) 기존 고정 문구로 폴백
  if (!esSync) {
    return <p>{PRODUCT_CATEGORY_LINK_BULK_GUIDE_TEXT.COMPLETED}</p>;
  }

  // 동기화할 상품이 없음 (성공 0건) — 안내할 것이 없다
  if (esSync.status === BULK_UPLOAD_ES_SYNC_STATUS.NOT_REQUESTED) {
    return null;
  }

  if (esSync.status === BULK_UPLOAD_ES_SYNC_STATUS.COMPLETED) {
    return <p>{PRODUCT_CATEGORY_LINK_BULK_GUIDE_TEXT.ES_SYNC_COMPLETED}</p>;
  }

  if (esSync.status === BULK_UPLOAD_ES_SYNC_STATUS.FAILED) {
    return (
      <p className="is-fail">
        일부 상품 {formatCount(esSync.failedProducts)}건의 검색 인덱스 반영에 실패했습니다.{' '}
        {PRODUCT_CATEGORY_LINK_BULK_GUIDE_TEXT.FAILED}
      </p>
    );
  }

  // pending / enqueued / processing — 진행 수치와 함께
  return (
    <p>
      검색 인덱스 반영 중 ({formatCount(esSync.completedProducts)} / {formatCount(esSync.totalProducts)}) —{' '}
      {PRODUCT_CATEGORY_LINK_BULK_GUIDE_TEXT.ES_SYNC_IN_PROGRESS}
    </p>
  );
};

/**
 * 상품-카테고리 링크 벌크 업로드 진행 상태 카드.
 *
 * props 로만 동작하는 표시 전용 컴포넌트라 배치(페이지 인라인 / 모달)와 결합되지 않는다.
 */
const ProductCategoryLinkBulkStatusCard = (props: ProductCategoryLinkBulkStatusCardProps): JSX.Element | null => {
  const { jobId, fileName, uploadPercent, job, isStatusUnavailable, isDownloadingFailedCsv, onDownloadFailedCsv, onDismiss } =
    props;

  // 파일 전송 중 — 30MB 는 수십 초가 걸릴 수 있어 전송률을 따로 보여준다
  if (uploadPercent !== null) {
    return (
      <div className="product-category-link-bulk-status-card">
        <div className="card-title">
          <span className="title-text">파일 업로드 중</span>
          <span className="file-name">{fileName}</span>
        </div>
        <Progress percent={uploadPercent} status="active" />
      </div>
    );
  }

  if (!jobId) {
    return null;
  }

  // 상태를 받지 못한 채 조회가 실패한 경우. 닫기를 제공하지 않으면 업로드 버튼이 잠긴 채로 갇힌다
  if (isStatusUnavailable) {
    return (
      <div className="product-category-link-bulk-status-card is-failed">
        <div className="card-title">
          <span className="title-text">업로드 상태를 확인할 수 없습니다</span>
          <span className="job-id">Job ID: {jobId}</span>
        </div>
        <p className="fail-reason">처리는 계속되고 있을 수 있습니다. 잠시 후 새로고침해 다시 확인해주세요.</p>
        <div className="card-guide">
          <p>{PRODUCT_CATEGORY_LINK_BULK_GUIDE_TEXT.FAILED}</p>
        </div>
        <div className="card-dismiss">
          <Button isRadius text="닫기" onClick={onDismiss} />
        </div>
      </div>
    );
  }

  // 202 를 받은 직후 첫 조회 응답이 오기 전
  if (!job) {
    return (
      <div className="product-category-link-bulk-status-card">
        <div className="card-title">
          <span className="title-text">업로드 상태 확인 중</span>
          <span className="job-id">Job ID: {jobId}</span>
        </div>
        <Progress percent={0} status="active" />
      </div>
    );
  }

  // 만료(expired)는 훅이 jobId 를 비우고 안내 토스트를 띄운다 — 카드는 그리지 않는다
  if (job.status === BULK_UPLOAD_JOB_EXPIRED) {
    return null;
  }

  if (job.status === BULK_UPLOAD_JOB_STATUS.COMPLETED) {
    return (
      <div className="product-category-link-bulk-status-card is-completed">
        <div className="card-title">
          <span className="title-text">업로드 완료</span>
          {fileName ? <span className="file-name">{fileName}</span> : null}
        </div>
        {job.result ? <ResultSummary result={job.result} /> : null}
        {job.result?.failedRowsAvailable ? (
          <div className="card-action">
            <Button
              color="primary"
              isRadius
              text={isDownloadingFailedCsv ? '다운로드 중...' : '실패 행 상세 다운로드 (CSV)'}
              disabled={isDownloadingFailedCsv}
              onClick={onDownloadFailedCsv}
            />
          </div>
        ) : null}
        <div className="card-guide">
          <EsSyncGuide esSync={job.esSync} />
        </div>
        <div className="card-dismiss">
          <Button isRadius text="닫기" onClick={onDismiss} />
        </div>
      </div>
    );
  }

  if (job.status === BULK_UPLOAD_JOB_STATUS.FAILED) {
    return (
      <div className="product-category-link-bulk-status-card is-failed">
        <div className="card-title">
          <span className="title-text">처리 실패</span>
          {fileName ? <span className="file-name">{fileName}</span> : null}
        </div>
        <p className="fail-reason">사유: {resolveBulkUploadErrorMessage(job.failedReason)}</p>
        {/* 운영 문의 시 추적용 */}
        <p className="fail-trace">
          Job ID: {job.jobId}
          {job.correlationId ? ` · 추적 ID: ${job.correlationId}` : ''}
        </p>
        <div className="card-guide">
          <p>{PRODUCT_CATEGORY_LINK_BULK_GUIDE_TEXT.FAILED}</p>
        </div>
        <div className="card-dismiss">
          <Button isRadius text="닫기" onClick={onDismiss} />
        </div>
      </div>
    );
  }

  /**
   * 완료·실패·만료가 아닌 나머지 전부 — waiting / active / delayed / paused / stuck,
   * 그리고 백엔드가 앞으로 추가할 수 있는 미지의 상태.
   * 여기서 null 을 반환하면 잡은 진행 중(isBusy)이라 업로드 버튼은 잠겨 있는데
   * 화면에는 아무것도 없는 상태가 되므로, 모르는 상태는 진행 중 카드로 폴백한다.
   */
  const isWaiting = job.status === BULK_UPLOAD_JOB_STATUS.WAITING;
  const isStuck = job.status === BULK_UPLOAD_JOB_STATUS.STUCK;

  return (
    <div className="product-category-link-bulk-status-card">
      <div className="card-title">
        <span className="title-text">{isWaiting ? '업로드 대기 중' : '업로드 처리 중'}</span>
        {fileName ? <span className="file-name">{fileName}</span> : null}
        <span className="job-id">Job ID: {job.jobId}</span>
      </div>
      <Progress percent={job.progress} status="active" />
      <div className="card-guide">
        {/* 큐 동시성이 1이라 다른 어드민의 업로드가 돌고 있으면 순번을 기다린다 */}
        {isWaiting ? <p>{PRODUCT_CATEGORY_LINK_BULK_GUIDE_TEXT.WAITING}</p> : null}
        {/* stalled 판정 — 워커가 재개하거나 실패로 넘어갈 때까지 잡은 살아 있다 */}
        {isStuck ? <p>{PRODUCT_CATEGORY_LINK_BULK_GUIDE_TEXT.STUCK}</p> : null}
        <p>{PRODUCT_CATEGORY_LINK_BULK_GUIDE_TEXT.IN_PROGRESS}</p>
      </div>
    </div>
  );
};

export default ProductCategoryLinkBulkStatusCard;
